import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, Link2, Repeat, Tag, Edit, Trash2, Video } from 'lucide-react';
import { looksLikeHtml, sanitizeCalendarHtml } from '@/lib/sanitize-calendar-html';
import { DeleteRecurringEventDialog } from './DeleteRecurringEventDialog';
import type { EventInstance, RecurrenceEditScope, TimeFormat } from '../types';
import {
  formatTimeRange,
  getEventColor,
  buildRecurrenceLabel,
  parseRruleToRule,
  detectVideoLink,
  VIDEO_PROVIDER_LABELS,
} from '../utils';

interface EventDetailDialogProps {
  open: boolean;
  event: EventInstance | null;
  onClose: () => void;
  onEdit: (event: EventInstance) => void;
  onDelete: (event: EventInstance, scope: RecurrenceEditScope) => void;
  timeFormat?: TimeFormat;
}

export function EventDetailDialog({
  open,
  event,
  onClose,
  onEdit,
  onDelete,
  timeFormat = '12h',
}: EventDetailDialogProps) {
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  if (!event) return null;

  const color = getEventColor(event);
  const rule = parseRruleToRule(event.rrule);
  const startDate = new Date(event.startAt);
  const videoCall = detectVideoLink(event);

  const handleDelete = (scope: RecurrenceEditScope) => {
    onDelete(event, scope);
    setScopeDialogOpen(false);
    onClose();
  };

  /**
   * One delete affordance, Google-style. A single event goes straight through
   * — deletion is soft (the event lands in Deleted events and can be restored
   * via POST /events/:id/restore), so an extra confirmation would tax the
   * common case to guard a reversible action. A series has to say WHICH
   * occurrences, so it opens the scope chooser instead.
   */
  const handleDeleteClick = () => {
    if (event.isRecurring) {
      setScopeDialogOpen(true);
      return;
    }
    handleDelete('all');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Radix focuses the first tabbable element when a dialog opens, and the
          delete control is now first in DOM order — so opening an event landed
          focus on Delete with a visible ring, looking pre-selected and putting
          a destructive action one Enter away. Send focus to the title instead:
          focus still enters the dialog (so the focus trap and screen-reader
          announcement behave), but on something inert. */}
      <DialogContent
        className="max-w-md max-h-[85vh] flex flex-col"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          titleRef.current?.focus();
        }}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div
              className="w-4 h-4 rounded-full mt-1 shrink-0"
              style={{ backgroundColor: color }}
            />
            {/* min-w-0: a flex child defaults to min-width:auto and refuses to
                shrink below its content, which silently defeats the title's
                break-words on a long synced title. */}
            {/* Reserve the top-right corner: the Dialog primitive renders its
                own close X at `right-4`, so the delete control sits at
                `right-12` to avoid overlapping it, and the title gets padding
                so a long synced title cannot run underneath either. */}
            <button
              type="button"
              onClick={handleDeleteClick}
              title={event.isRecurring ? 'Delete\u2026' : 'Delete event'}
              aria-label={event.isRecurring ? 'Delete recurring event' : 'Delete event'}
              className="absolute right-12 top-4 rounded-sm p-0.5 text-muted-foreground opacity-70 transition-opacity hover:text-destructive hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <Trash2 className="h-4 w-4" />
            </button>

            <div className="min-w-0 pr-16">
              <DialogTitle
                ref={titleRef}
                tabIndex={-1}
                className="text-lg break-words outline-none"
              >
                {event.title}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {formatTimeRange(event.startAt, event.endAt, event.isAllDay, timeFormat)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3 py-2">
            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 shrink-0" />
              {startDate.toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>

            {/* Time */}
            {!event.isAllDay && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                {formatTimeRange(event.startAt, event.endAt, false, timeFormat)}
              </div>
            )}

            {/* Location — synced events carry full postal addresses and pasted
              conferencing URLs, which have no break opportunities. Without
              `min-w-0` a flex child refuses to shrink below its content width,
              so the text runs outside the dialog instead of wrapping. */}
            {event.location && (
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                <span className="min-w-0 break-words">{event.location}</span>
              </div>
            )}

            {/* Recurrence */}
            {event.isRecurring && rule && (
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <Repeat className="h-4 w-4 shrink-0 mt-0.5" />
                <span className="min-w-0 break-words">{buildRecurrenceLabel(rule)}</span>
              </div>
            )}

            {/* Video call / URL */}
            {videoCall ? (
              <div className="flex items-center gap-2 text-sm">
                <Video className="h-4 w-4 shrink-0 text-muted-foreground" />
                <a
                  href={videoCall.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                >
                  {VIDEO_PROVIDER_LABELS[videoCall.provider]}
                </a>
              </div>
            ) : event.url && /^https?:\/\//i.test(event.url) ? (
              <div className="flex items-start gap-2 text-sm">
                <Link2 className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
                {/* Wrap rather than truncate. This is a DETAILS view, so clipping
                  the link to one line hides the thing the user opened the
                  dialog to read (and makes it uncopyable). `break-all` because
                  URLs have no natural break opportunities; `min-w-0` so the
                  flex child is allowed to shrink below its content width. */}
                <a
                  href={event.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-w-0 break-all text-primary hover:underline"
                >
                  {event.url}
                </a>
              </div>
            ) : null}

            {/* Tags */}
            {event.tags.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Tag className="h-4 w-4 shrink-0" />
                <div className="flex min-w-0 flex-wrap gap-1">
                  {event.tags.map((tag) => (
                    <span key={tag} className="bg-muted px-2 py-0.5 rounded text-xs break-words">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Description.
              Providers write RICH HTML here — a Google/Outlook invite is
              typically a <table> of dial-in details, a <ul> agenda and an
              anchor to the meeting link. Rendering that as text printed every
              tag literally, which is what "garbled" was.
              Plain-text descriptions keep the `whitespace-pre-wrap` path:
              their line breaks are newlines, not <br>, and routing them
              through the sanitizer would collapse them.
              `break-words` stays on both — pre-wrap does not break long
              unbroken tokens, and these descriptions carry 70+ character
              meeting URLs. */}
            {event.description &&
              (looksLikeHtml(event.description) ? (
                <div
                  data-testid="event-description"
                  className={[
                    'text-sm mt-2 break-words text-muted-foreground border-t pt-3',
                    // The sanitizer keeps structure; these give it back the
                    // shape the browser's UA stylesheet would, since Tailwind's
                    // preflight resets lists and tables to nothing.
                    '[&_p]:mb-2 [&_p:last-child]:mb-0',
                    '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2',
                    '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2',
                    '[&_li]:mb-0.5',
                    '[&_a]:underline [&_a]:text-primary [&_a]:break-all',
                    '[&_strong]:font-semibold [&_b]:font-semibold',
                    // Headings an organiser wrote must not out-shout the
                    // dialog's own; render them at body size, just bolder.
                    '[&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-sm [&_h4]:text-sm',
                    '[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h4]:font-semibold',
                    // A dial-in table is wider than a 28rem dialog. Let the TABLE
                    // scroll inside the panel rather than the panel scroll
                    // sideways — wide content gets its own overflow container.
                    '[&_table]:block [&_table]:w-full [&_table]:overflow-x-auto',
                    '[&_table]:border-collapse [&_table]:mb-2',
                    '[&_th]:text-left [&_th]:font-medium [&_th]:pr-3 [&_th]:align-top',
                    '[&_td]:pr-3 [&_td]:align-top',
                  ].join(' ')}
                  // Sanitized against a no-CSS, no-image allow-list immediately
                  // above; see sanitize-calendar-html.ts for why this is inline
                  // rather than the email module's iframe.
                  dangerouslySetInnerHTML={{ __html: sanitizeCalendarHtml(event.description) }}
                />
              ) : (
                <div
                  data-testid="event-description"
                  className="text-sm mt-2 whitespace-pre-wrap break-words text-muted-foreground border-t pt-3"
                >
                  {event.description}
                </div>
              ))}

            {/* Calendar name */}
            {event.calendarName && (
              <div className="text-xs text-muted-foreground mt-2 break-words">
                Calendar: {event.calendarName}
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          {/* Delete lives in the header now (one icon, Google-style). Three
              permanent red buttons made every event — including
              non-recurring ones, which have no scope to choose — read as
              dangerous. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onEdit(event);
              onClose();
            }}
          >
            <Edit className="h-3.5 w-3.5 mr-1" />
            Edit
          </Button>
        </DialogFooter>

        <DeleteRecurringEventDialog
          open={scopeDialogOpen}
          title={event.title}
          onCancel={() => setScopeDialogOpen(false)}
          onConfirm={handleDelete}
        />
      </DialogContent>
    </Dialog>
  );
}
